import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import * as R from 'ramda';
import { arrayOf, func, shape, string } from 'prop-types';
import { useUserRolePermissions } from 'hooks';
import { renderInput } from '../../utils/renderUtils';
import { useVerifyControlNumberQuery } from '../../customerAuthorizationsEndpoints';
import LoadingControlDetails from './LoadingControlDetails';

const Destinations = ({ info, setInfo, setValidation, validation }) => {
  const { controlNumbers = [], terminals } = info;
  const { currentAffiliate } = useUserRolePermissions();

  const [loadingControlIdName, setLoadingControlIdName] = useState('');
  const [message, setMessage] = useState('');

  const params = {
    loadingControlIdName,
    supplier: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
    terminals,
  };

  const skip = R.lt(loadingControlIdName.length, 1);
  const { data: verifiedControlId = {} } = useVerifyControlNumberQuery({ params }, { skip });

  const handleChange = (_, __, reason) => {
    if (reason === 'clear') {
      setInfo(R.assoc('controlNumbers', [], info));
    }
  };

  const onDelete = (value, index) => {
    R.compose(
      setInfo,
      R.assoc('controlNumbers'),
      R.always(R.reject(R.propEq('idName', value), controlNumbers))
    )(value);

    R.compose(
      setValidation,
      R.assoc('controlNumbers'),
      R.always(R.remove(index, 1, R.prop('controlNumbers', validation)))
    )(index);
  };

  // Given to LoadingControlDetails for updating the state of a specific controlNumber.
  // setControlNumberState :: i -- index of controlNumber to modify
  //                  -> f -- function to transform controlNumber
  //                  -> ()
  const setControlNumberState = R.curry((i, f) => {
    setInfo(state => R.assoc('controlNumbers', R.adjust(i, f, state.controlNumbers), state));
  });

  // Given to LoadingControlDetails for tracking the validation state of a specific controlNumber.
  // setControlNumValidationState :: i -- index of controlNumber to modify
  //                  -> value -- value to assign to given index in validation state
  //                  -> ()
  const setControlNumValidationState = R.curry((i, value) => {
    setValidation(state =>
      R.assoc('controlNumbers', R.update(i, value, state.controlNumbers), state)
    );
  });

  const renderTags = R.curry((tags, getTagProps) =>
    tags.map((item, index) => (
      <Chip
        {...getTagProps(item)}
        key={item}
        label={item}
        onDelete={() => onDelete(item, index)}
        sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
        variant="outlined"
      />
    ))
  );

  const hasError = R.or(
    R.isEmpty(controlNumbers),
    R.prop('controlNumbers', validation).some(msg => !R.isEmpty(msg))
  );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        aria-controls="customer-customerAuth-content"
        expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="body2">Loading Control Numbers</Typography>
          {!!controlNumbers.length && (
            <Chip label={controlNumbers.length} type="accordion" variant="badge" />
          )}
          {hasError && <ErrorRoundedIcon sx={{ color: 'error.main' }} />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography>
          At least one Loading Control Number must be selected for the authorization. Loading
          Control Numbers can be configured with or without a destination.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridGap: 16,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
            mt: 2,
          }}
        >
          <Autocomplete
            freeSolo
            getOptionLabel={R.prop('idName')}
            inputValue={loadingControlIdName}
            multiple
            onChange={handleChange}
            onFocus={() => setMessage('')}
            onInputChange={(event, newValue) => {
              // Only allow numbers
              const numericValue = newValue.replace(/[^0-9]/g, '');
              R.ifElse(R.equals('0'), R.identity, setLoadingControlIdName)(numericValue);
            }}
            options={[]}
            renderInput={renderInput({
              // Add numeric input restriction
              inputProps: {
                inputMode: 'numeric',
                onKeyPress: e => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                },
                pattern: '[0-9]*',
              },
              label: 'Loading Control ID',
              maxLength: 14,
            })}
            renderTags={renderTags}
            value={R.pluck('idName', controlNumbers)}
          />
          <Button
            disabled={skip}
            onClick={() => {
              const index = R.findIndex(R.equals(loadingControlIdName), verifiedControlId);
              if (verifiedControlId.doesExist) {
                setMessage(
                  'This loading control number already exists. Please enter a new loading control number.'
                );
              } else if (index > -1) {
                setMessage('This loading control number is currently in the list');
              } else {
                setInfo(
                  R.assoc(
                    'controlNumbers',
                    [...controlNumbers, { idName: loadingControlIdName }],
                    info
                  )
                );
                setMessage('');
                setLoadingControlIdName('');
                setValidation(state =>
                  R.assoc('controlNumbers', R.append('', state.controlNumbers), state)
                );
              }
            }}
            sx={{
              '&.MuiButtonBase-root.Mui-disabled': {
                backgroundColor: 'graphic.light',
              },
              alignSelf: 'center',
            }}
          >
            Add
          </Button>
          <Typography sx={{ gridColumn: { lg: '1 / 3' }, gridRow: { lg: '2 / 3' } }}>
            {message}
          </Typography>
        </Box>
        <Stack mt={2} spacing={2}>
          {controlNumbers.map((controlNumber, index) => (
            <LoadingControlDetails
              controlNumber={controlNumber}
              info={info}
              key={controlNumber.idName}
              setControlNumberState={setControlNumberState(index)}
              setInfo={setInfo}
              setValidationState={setControlNumValidationState(index)}
              validationState={validation?.controlNumbers[index]}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

Destinations.propTypes = {
  info: shape({
    controlNumbers: arrayOf(shape({})),
  }),
  setInfo: func.isRequired,
  setValidation: func.isRequired,
  validation: shape({ controlNumbers: arrayOf(string) }).isRequired,
};

Destinations.defaultProps = {
  info: {
    controlNumbers: [],
  },
};

export default Destinations;
